import { CONSULTANT_SETUP_OPTIONS } from "./consultant-setup-options.ts";
import { API_CONSULTANT_SETUP } from "../../../../../config/Endpoints.js";
import { putApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";

export async function saveConsultantSetup(id: unknown, stage: "IT" | "HR", payload: Record<string, unknown>) {
  if (!/^\d+$/.test(String(id)) || Number(id) <= 0) throw new Error("Missing consultant ID.");
  return putApi<{ message?: string }>(`${API_CONSULTANT_SETUP}/${encodeURIComponent(String(id))}/${stage.toLowerCase()}-setup`, accessToken(), payload);
}

export function buildItSetupPayload(email: string, password: string) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) throw new Error("Enter a valid Caelius email.");
  if (!password.trim()) throw new Error("Enter a temporary password.");
  return { officialEmail: email.trim(), temporaryPassword: password };
}

export const HR_TEXT_FIELDS: ReadonlyArray<readonly [string, string, ("date" | "number")?]> = [
  ["rolePosition", "Role / Position"],
  ["clientProject", "Client / Project"], ["descriptionOfServices", "Description of Services"],
  ["contractStartDate", "Contract Start Date", "date"], ["contractEndDate", "Contract End Date", "date"],
  ["consultantPayRate", "Consultant Pay Rate", "number"], ["consultantPayRateType", "Consultant Pay Rate Type"],
  ["caeliusBillRate", "Caelius Bill Rate", "number"], ["caeliusBillRateType", "Caelius Bill Rate Type"],
  ["expectedHoursSchedule", "Expected Hours / Schedule"], ["workMode", "Work Mode"], ["poSowNumber", "PO / SOW Number"],
  ["noticePeriodDays", "Notice Period (Days)", "number"], ["dob", "Date of Birth", "date"],
  ["doj", "Date of Joining", "date"], ["gender", "Gender"], ["permanentAddress", "Permanent Address"],
] as const;

export const INDIAN_HIDDEN_OFFICE_FIELDS = new Set([
  "leaveManagerId", "region", "caeliusProjectContactId",
  "consultantPayRate", "consultantPayRateType", "caeliusBillRate", "caeliusBillRateType",
  "expectedHoursSchedule", "workMode", "poSowNumber", "permanentAddress", "gender",
  "rolePosition", "clientProject", "descriptionOfServices",
]);

export const OPTIONAL_HR_SETUP_FIELDS = new Set(["contractEndDate", "dob"]);

export function validateHrSetupFields(values: Record<string, string>, consultantType: string): Record<string, string> {
  const errors: Record<string, string> = {};
  const isIndian = consultantType === "INDIAN_CONSULTANT";
  const fields: ReadonlyArray<readonly [string, string, string?]> = [
    ["departmentId", "Department", "id"], ["designationId", "Designation", "id"],
    ["reportingManagerId", "Reporting Manager", "id"], ["leaveManagerId", "Leave Manager", "id"],
    ["caeliusProjectContactId", "Caelius Project Contact", "id"],
    ...HR_TEXT_FIELDS, ["techStack", "Tech Stack"],
  ];
  for (const [key, label, type] of fields) {
    if (isIndian && INDIAN_HIDDEN_OFFICE_FIELDS.has(key)) continue;
    const value = values[key]?.trim() ?? "";
    if (!value) {
      if (!OPTIONAL_HR_SETUP_FIELDS.has(key)) errors[key] = `${label} is required.`;
      continue;
    }
    if (type === "id" && (!/^\d+$/.test(value) || Number(value) <= 0)) errors[key] = `Select a valid ${label.toLowerCase()}.`;
    if (type === "number" && (!Number.isFinite(Number(value)) || Number(value) < 0 || (key === "noticePeriodDays" && !Number.isInteger(Number(value))))) errors[key] = `Enter a valid ${label.toLowerCase()}.`;
    if (type === "date") {
      const date = new Date(`${value}T00:00:00Z`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) errors[key] = `Enter a valid ${label.toLowerCase()}.`;
    }
    if (CONSULTANT_SETUP_OPTIONS[key] && !CONSULTANT_SETUP_OPTIONS[key].some((option) => option.value === value)) errors[key] = `Select a valid ${label.toLowerCase()}.`;
  }
  if (values.contractStartDate && values.contractEndDate && values.contractEndDate < values.contractStartDate) errors.contractEndDate = "Contract end date must be on or after the start date.";
  return errors;
}

export function buildHrSetupPayload(values: Record<string, string>, tech: boolean, decision: string, rejectionReason: string, consultantType?: string) {
  if (decision === "REJECTED") {
    if (!rejectionReason.trim()) throw new Error("Enter a rejection reason.");
    return { decision, rejectionReason: rejectionReason.trim() };
  }
  const isIndian = consultantType === "INDIAN_CONSULTANT";
  const region =
    consultantType === "INDIAN_CONSULTANT"
      ? "CCI_INDIA"
      : consultantType === "DIRECT" || consultantType === "VENDOR_BASED"
        ? "CCI_US"
        : ""; const payload: Record<string, unknown> = { decision: "APPROVED", rejectionReason: null, tech };
  const requiredIds = [
    ["reportingManagerId", "reporting manager"],
    ["designationId", "designation"],
    ["leaveManagerId", "leave manager"],
    ["caeliusProjectContactId", "Caelius project contact"],
  ] as const;
  for (const [key, label] of requiredIds) {
    if (isIndian && INDIAN_HIDDEN_OFFICE_FIELDS.has(key)) continue;
    if (!/^\d+$/.test(values[key] ?? "") || Number(values[key]) <= 0) throw new Error(`Select a valid ${label}.`);
    payload[key] = Number(values[key]);
  }
  if (!region) {
    throw new Error("Invalid consultant type.");
  }

  payload.region = region;
  for (const [key, label, type] of HR_TEXT_FIELDS) {
    if (isIndian && INDIAN_HIDDEN_OFFICE_FIELDS.has(key)) continue;
    const value = values[key]?.trim() ?? "";
    if (value && CONSULTANT_SETUP_OPTIONS[key] && !CONSULTANT_SETUP_OPTIONS[key].some((option) => option.value === value)) throw new Error(`Select a valid ${label.toLowerCase()}.`);
    if (type === "number" && value && (!Number.isFinite(Number(value)) || Number(value) < 0 || (key === "noticePeriodDays" && !Number.isInteger(Number(value))))) throw new Error(`Enter a valid ${label}.`);
    payload[key] = value ? type === "number" ? Number(value) : value : null;
  }
  if (values.contractStartDate && values.contractEndDate && values.contractEndDate < values.contractStartDate) throw new Error("Contract end date must be on or after the start date.");
  if (tech && !values.techStack) throw new Error("Select a tech stack.");
  payload.techStack = tech ? values.techStack : null;
  return payload;
}
